import { Outlet, useLocation, useNavigate, useMatch, useSearchParams } from 'react-router-dom'
import { Formik } from 'formik'
import { Button, TextField, Alert, CircularProgress } from '@mui/material'
import { validateEmail } from 'utils/api/auth'
import { useEffect, useState } from 'react'
import {
  REQUEST_APPOINTMENT_KEY,
  APP_VERSION,
  REDIRECT_KEY,
  SECURE_STORAGE_KEY,
  WEBSITE_SELECTED_PRODUCTS,
  HAS_PREVIOUSLY_LOGGEDIN,
  ORDER_FLOW_KEY,
} from 'app-constants'
import yup from 'utils/yup'
import { ReactComponent as Logo } from 'assets/icons/logo.svg'
import { Capacitor } from '@capacitor/core'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import LoginPassword from './password'
import { useStoreData } from 'hooks/useLocalData'
import { ProductSearchResult } from 'types/treatments'

const loginSchema = yup.object().shape({
  identity: yup.string().email('enter a valid email').required('please enter email'),
})

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loginError, setLoginError] = useState('')
  const [storedCredentials, setStoredCredentials] = useState<{ email: string; password: string }>()
  const [isLogginIn, setIsLogginIn] = useState(false)
  const isPage = useMatch('/login')
  const productsParam = searchParams.get('products')
  const location = useLocation()
  const storeData = useStoreData(ORDER_FLOW_KEY)
  const appointmentRequestUrl = sessionStorage?.[REQUEST_APPOINTMENT_KEY]
    ? JSON.parse(sessionStorage?.[REQUEST_APPOINTMENT_KEY])
    : ''

  const redirectUrl =
    (location.state as any)?.redirect ||
    sessionStorage.getItem(REDIRECT_KEY) ||
    appointmentRequestUrl?.pathname ||
    '/'

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SecureStorage.get(SECURE_STORAGE_KEY)
        .then((result: any) => {
          setStoredCredentials(result)
        })
        .catch()
    }
    if (productsParam) {
      const parsed = (JSON.parse(productsParam) as ProductSearchResult[])?.map((p) => {
        return {
          ...p,
          quantity: 1,
          total_order: 1,
        }
      })
      storeData({
        selectedProducts: parsed,
        results: parsed,
      })
      sessionStorage.setItem(WEBSITE_SELECTED_PRODUCTS, productsParam)
    }
  }, [])

  async function handleLogin(values: { identity: string }) {
    try {
      setIsLogginIn(true)
      setLoginError('')
      const res = await validateEmail(values)

      const isOnPlatForm = res.platforms.some((p) => p.service_name === 'eazymed')

      if (!res.is_registered) {
        navigate(`/signup?identity=${values.identity}`)
        return
      }

      if (res.is_registered) {
        navigate(
          `?email=${values.identity}&isOnPlatForm=${JSON.stringify(
            isOnPlatForm
          )}&platforms=${res.platforms
            ?.map((p) => p.service_name)
            ?.join(',')}&products=${productsParam}`,
          {
            state: { redirect: redirectUrl },
          }
        )
        return
      }
    } catch (error: any) {
      setLoginError(error.message)
    } finally {
      setIsLogginIn(false)
      sessionStorage.removeItem(REQUEST_APPOINTMENT_KEY)
    }
  }

  return (
    <div className="h-fit">
      <Outlet />
      {isPage &&
        (searchParams.get('email') ||
        localStorage[HAS_PREVIOUSLY_LOGGEDIN] === 'true' ||
        storedCredentials?.email ? (
          <LoginPassword />
        ) : (
          <div className="lg:bg-white lg:shadow lg:p-10 p-5 lg:w-[35rem] lg:rounded-3xl">
            <div className="self-center grid place-items-center mb-1">
              <a
                aria-label="Eazymed logo to go to Eazymed website"
                href={import.meta.env.VITE_EAZYMED_WEBSITE_URL}
                target="_blank"
                rel="noreferrer"
              >
                <Logo className="text-8xl w-[13rem] h-[8.93rem]" />
              </a>
            </div>
            <div className="mb-12 grid place-items-center">
              <h1 className="lg:text-center text-left title-mobile lg:title-desktop">Welcome 😃</h1>
            </div>
            <Formik
              initialValues={{ identity: (appointmentRequestUrl?.email as string) || '' }}
              validationSchema={loginSchema}
              onSubmit={handleLogin}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                isSubmitting,
                setValues,
              }) => (
                <form onSubmit={handleSubmit}>
                  <p className="mb-2 text-sm font-semibold">Your email address</p>
                  <TextField
                    id="identity"
                    type="email"
                    className="mb-8"
                    value={values.identity}
                    onChange={handleChange}
                    error={touched.identity && !!errors.identity}
                    helperText={touched.identity ? errors.identity : ''}
                    inputProps={{
                      className: 'rounded-[11px]',
                    }}
                    fullWidth
                  />

                  <div className="flex items-center gap-4 mt-8">
                    <Button
                      type="submit"
                      disabled={isSubmitting || isLogginIn}
                      className="p-3 rounded-md"
                      fullWidth
                    >
                      {isSubmitting || isLogginIn ? (
                        <>
                          <CircularProgress color="inherit" className="text-white" size={22} />
                          <span className="sr-only">Submitting</span>
                        </>
                      ) : (
                        'CONTINUE'
                      )}
                    </Button>
                  </div>
                  <Alert
                    severity="error"
                    className={`mt-4 ${!loginError ? 'opacity-0' : ''}`}
                    aria-hidden={!loginError}
                    data-test-id="login-server-error"
                  >
                    {loginError}
                  </Alert>

                  {Capacitor.isNativePlatform() && (
                    <div className="text-center mt-10">
                      <span className="text-xs">v</span>
                      {`${APP_VERSION}`}
                    </div>
                  )}
                </form>
              )}
            </Formik>
          </div>
        ))}
    </div>
  )
}

export default Login
